// import { Plus } from "lucide-react";
import { z } from "zod";
import Cookies from "js-cookie";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MdClose } from "react-icons/md";

import {
  AlertDialog,
  AlertDialogContent,
} from "@/components/ui/alert-dialog/alert-dialog";
import { Button } from "@/components/ui/button/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { Textarea } from "@/components/ui/textarea/textarea";
import { createNewPerformanceLogOfEmployee } from "@/config/mutation/admin";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { useCreatePerformanceLog } from "./use-create-performance-log-modal";

const formSchema = z.object({
  reasonPositive: z.string(),
  reasonNegative: z.string(),
  deduction: z.coerce.number().default(0),
  addition: z.coerce.number().default(0),
});

export type FormValues = z.infer<typeof formSchema>;

export const CreatePerformanceLog = () => {
  const user = Cookies.get("key") as string;
  const { open, onOpenChange } = useCreatePerformanceLog();
  const queryClient = useQueryClient();

  const { empId } = useParams() as {
    empId: string;
  };

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reasonNegative: "",
      reasonPositive: "",
      deduction: 0,
      addition: 0,
    },
  });

  const { mutate, isPending: isSubmitting } = useMutation({
    mutationFn: createNewPerformanceLogOfEmployee(user),
  });

  // 2. Define a submit handler.
  function onSubmit(values: FormValues) {
    // console.log(values);

    if (values?.addition === 0 && values?.deduction === 0) {
      return toast.error("Addition and Deduction both points can't be zero.");
    }

    mutate(
      {
        employee: empId,
        deduction: values.deduction,
        addition: values.addition,
        reasonNegative: values.reasonNegative,
        reasonPositive: values.reasonPositive,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["performance-logs"],
          });
          await queryClient.invalidateQueries({
            queryKey: ["employee", empId],
          });

          toast.success("New Performance log Created!");
          onOpenChange(false);

          form.reset();
        },

        onError: (error) => {
          console.log("ERROR WHILE CREATING PERFORMANCE LOGS", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  }

  return (
    <>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        {/* <AlertDialogTrigger>
          <Button className="flex items-center gap-2" size="sm">
            <Plus className="w-4 h-4" />
            New Performance Log
          </Button>
        </AlertDialogTrigger> */}
        <AlertDialogContent className="p-0 max-w-2xl">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="border-b px-3 py-2 flex items-center justify-between">
                <h3 className="text-xl font-medium tracking-normal">
                  Create Performance Log
                </h3>
                <div className="relative flex items-center gap-4">
                  <Button size="sm" disabled={isSubmitting}>
                    Create
                  </Button>
                  <button
                    disabled={isSubmitting}
                    type="button"
                    className="p-1 rounded-md transition-all duration-300 hover:bg-accent"
                    onClick={() => {
                      onOpenChange(false);
                      form.reset();
                    }}
                  >
                    <MdClose className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-2 p-5">
                <FormField
                  control={form.control}
                  name="reasonPositive"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Positive Reason</FormLabel>
                      <FormControl>
                        <Textarea rows={4} disabled={isSubmitting} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="addition"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Addition Point</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          disabled={isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="reasonNegative"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Negative Reason</FormLabel>
                      <FormControl>
                        <Textarea rows={4} disabled={isSubmitting} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="deduction"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Deduction Point</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          disabled={isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
